import { RADIUS } from "@elyxeer/ui";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import { DayDoses } from "~/components/routine/DayDoses";
import { RoutineWeek } from "~/components/routine/RoutineWeek";
import {
  useRoutineCheckIns,
  useRoutineStreak,
} from "~/components/routine/use-routine";
import { SectionHeading } from "~/components/ui/SectionHeading";
import { dayKey } from "~/lib/calendar";
import { indexCheckIns, recentDays, ROUTINE_PATH } from "~/lib/routine";
import { useToday } from "~/lib/use-today";

/** A week: long enough to see a habit forming, short enough to fit a phone's width. */
const STRIP_DAYS = 7;

/**
 * The card width at which the week moves beside today's doses instead of under them.
 *
 * Measured on the card, not the viewport, because the dashboard gives it two very
 * different slots: a phone-width column beside the feed on a wide screen, the full
 * page width on a narrower one. Stacked at full width, two tiles and seven pips
 * stretched across 900px read as an empty form; side by side they fill the row at the
 * same height the stacked card has in the narrow slot.
 */
const SIDE_BY_SIDE = "@600";

/**
 * "My routine" on the dashboard: today's two doses and the week behind them.
 *
 * First on the dashboard because it is the one section that changes every day and asks
 * something of the member twice a day — everything else there is read, this is done.
 * Ticking happens right here, without a detour; the history and the reminders are one
 * tap further in, on the routine screen.
 */
export function RoutineCard() {
  const { t } = useTranslation();
  const today = useToday();
  const days = recentDays(today, STRIP_DAYS);

  const { data, isPending, isError, refetch } = useRoutineCheckIns(
    days[0],
    today,
  );
  const { data: streak } = useRoutineStreak(today);
  const index = indexCheckIns(data ?? []);

  return (
    <Box
      data-testid="routine.section"
      sx={{ display: "flex", flexDirection: "column", gap: { xs: 2, md: 3 } }}
    >
      <SectionHeading>{t("routine.title")}</SectionHeading>

      <Card
        variant="outlined"
        data-testid="routine.card"
        sx={{
          borderRadius: `${RADIUS.lg}px`,
          padding: { xs: 2, md: 3 },
          // The container `SIDE_BY_SIDE` measures.
          containerType: "inline-size",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              "@": "minmax(0, 1fr)",
              [SIDE_BY_SIDE]: "repeat(2, minmax(0, 1fr))",
            },
            alignItems: "center",
            columnGap: 4,
            rowGap: { xs: 2, md: 2.5 },
          }}
        >
          <DayDoses
            day={today}
            today={today}
            doses={index.get(dayKey(today))}
            // Unread is not "not yet": until the day is known the tiles claim nothing.
            loading={isPending || isError}
          />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: 2, md: 2.5 },
            }}
          >
            {isError ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Typography variant="T3" sx={{ color: "text.muted" }}>
                  {t("routine.error")}
                </Typography>
                <Button size="small" onClick={() => void refetch()}>
                  {t("common.retry")}
                </Button>
              </Box>
            ) : (
              <RoutineWeek days={days} index={index} today={today} />
            )}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Typography
                variant="L2"
                sx={{ color: "text.secondary" }}
                data-testid="routine.streak"
              >
                {streak
                  ? t("routine.streakValue", { days: streak.days })
                  : null}
              </Typography>

              <Button
                component={RouterLink}
                to={ROUTINE_PATH}
                size="small"
                endIcon={<ArrowForwardIcon sx={{ fontSize: "1rem" }} />}
                data-testid="routine.open"
                sx={{ color: "link.main", flexShrink: 0 }}
              >
                {t("routine.openHistory")}
              </Button>
            </Box>
          </Box>
        </Box>
      </Card>
    </Box>
  );
}
